import { UserPlus, MailCheck, FileText, Wallet } from "lucide-react";

/**
 * 7-day activation funnel: signup → email verified → CV uploaded → paid.
 * Each subsequent step is a strict subset of the previous. Bar widths are
 * relative to the top-of-funnel signup count so admins can spot the biggest
 * drop-off in one glance.
 */

const STEPS = [
  { key: "signups", label: "Aanmelding", Icon: UserPlus, tone: "bg-neutral-900" },
  { key: "verified", label: "E-mail bevestigd", Icon: MailCheck, tone: "bg-neutral-700" },
  { key: "uploaded_cv", label: "CV geüpload", Icon: FileText, tone: "bg-emerald-700" },
  { key: "paid", label: "Betaald / actief", Icon: Wallet, tone: "bg-emerald-800" },
];

const fmtPct = (cur, base) => {
  if (!base) return "—";
  const pct = (cur / base) * 100;
  return `${pct.toFixed(1)}%`;
};

export default function AdminActivationFunnelWidget({ funnel }) {
  if (!funnel) return null;
  const top = Math.max(1, funnel.signups || 0);

  return (
    <section className="mt-4" data-testid="admin-activation-funnel">
      <div className="ed-card p-6">
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <div>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
              Conversie-funnel — laatste 7 dagen
            </p>
            <p className="font-serif text-2xl mt-1" data-testid="funnel-top-count">
              {funnel.signups || 0} nieuwe aanmeldingen
            </p>
          </div>
          <p className="font-mono text-[0.6rem] text-neutral-500 hidden sm:block">
            waar haakt de gebruiker af?
          </p>
        </div>

        <div className="space-y-3">
          {STEPS.map(({ key, label, Icon, tone }, i) => {
            const count = funnel[key] || 0;
            const widthPct = Math.max(2, Math.min(100, (count / top) * 100));
            const prevCount = i === 0 ? count : (funnel[STEPS[i - 1].key] || 0);
            const cvrLabel = i === 0 ? "100%" : fmtPct(count, prevCount);
            return (
              <div key={key} className="flex items-center gap-4" data-testid={`activation-step-${key}`}>
                <div className="w-40 shrink-0 inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-600">
                  <Icon size={12} strokeWidth={1.5} /> {label}
                </div>
                <div className="flex-1 relative h-8 bg-neutral-100 border border-neutral-200 overflow-hidden">
                  <div
                    className={`h-full ${tone} transition-all duration-500 ease-out`}
                    style={{ width: `${widthPct}%` }}
                  />
                  <span
                    className="absolute inset-0 flex items-center px-3 font-mono text-xs mix-blend-difference"
                    style={{ color: "#F9F8F6" }}
                  >
                    {count.toLocaleString()}
                  </span>
                </div>
                <div className="w-16 text-right font-mono text-[0.7rem] text-neutral-500 shrink-0" data-testid={`activation-cvr-${key}`}>
                  {cvrLabel}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
